import { createAuthAxios, handleApiError } from "../../utils/apiUtils";
import { API_BASE_URL } from "../../config/apiConfig";

/**
 * Research Room Discovery Service
 * State-of-the-art recommendation engine for discovering research rooms
 */
class RoomDiscoveryService {
  /**
   * Get discovery homepage with all sections
   * @returns {Promise} All discovery sections (forYou, trending, hot, new, network, similar)
   */
  async getHomepage() {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.get(
        `${API_BASE_URL}/research-rooms/discover`
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get personalized "For You" feed
   * @param {Object} filters - { page, limit, diversity }
   */
  async getForYou(filters = {}) {
    try {
      const { page = 1, limit = 20, diversity = 0.3 } = filters;
      const authAxios = createAuthAxios();
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        diversity: diversity.toString(),
      });
      const response = await authAxios.get(
        `${API_BASE_URL}/research-rooms/discover/for-you?${params}`
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get trending rooms
   * @param {Object} filters - { limit, period }
   */
  async getTrending(filters = {}) {
    try {
      const { limit = 10, period = "week" } = filters;
      const authAxios = createAuthAxios();
      const params = new URLSearchParams({
        limit: limit.toString(),
        period,
      });
      const response = await authAxios.get(
        `${API_BASE_URL}/research-rooms/discover/trending?${params}`
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get hot rooms (most active right now)
   * @param {Number} limit - Number of rooms to fetch
   */
  async getHot(limit = 10) {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.get(
        `${API_BASE_URL}/research-rooms/discover/hot?limit=${limit}`
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get new rooms
   * @param {Number} limit - Number of rooms
   * @param {Number} maxAge - Max age in days
   */
  async getNew(limit = 10, maxAge = 14) {
    try {
      const authAxios = createAuthAxios();
      const params = new URLSearchParams({
        limit: limit.toString(),
        maxAge: maxAge.toString(),
      });
      const response = await authAxios.get(
        `${API_BASE_URL}/research-rooms/discover/new?${params}`
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get rooms from user's network
   * @param {Number} limit - Number of rooms
   */
  async getFromNetwork(limit = 10) {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.get(
        `${API_BASE_URL}/research-rooms/discover/network?limit=${limit}`
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get similar rooms to user's current rooms
   * @param {Number} limit - Number of rooms
   */
  async getSimilar(limit = 10) {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.get(
        `${API_BASE_URL}/research-rooms/discover/similar?limit=${limit}`
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Search rooms with smart ranking
   * @param {String} query - Search query
   * @param {Object} filters - { page, limit, field }
   */
  async search(query, filters = {}) {
    try {
      const { page = 1, limit = 20, field } = filters;
      const authAxios = createAuthAxios();
      const params = new URLSearchParams({
        q: query,
        page: page.toString(),
        limit: limit.toString(),
      });
      if (field) params.append("field", field);

      const response = await authAxios.get(
        `${API_BASE_URL}/research-rooms/discover/search?${params}`
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get rooms by field
   * @param {String} field - Field name
   * @param {Object} filters - { page, limit }
   */
  async getByField(field, filters = {}) {
    try {
      const { page = 1, limit = 20 } = filters;
      const authAxios = createAuthAxios();
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      const response = await authAxios.get(
        `${API_BASE_URL}/research-rooms/discover/field/${encodeURIComponent(field)}?${params}`
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get available fields with stats
   */
  async getFields() {
    try {
      const response = await fetch(
        `${API_BASE_URL}/research-rooms/discover/fields`
      );
      return await response.json();
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Refresh discovery cache
   */
  async refreshCache() {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.post(
        `${API_BASE_URL}/research-rooms/discover/refresh`
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }
}

const roomDiscoveryService = new RoomDiscoveryService();
export default roomDiscoveryService;
